/**
 * Image-transition registry.
 *
 * Single source of truth for the available results-page image transitions.
 * The admin Settings page reads this list to populate its picker; the
 * results card reads the user's selected default through AppSettingsContext.
 *
 * Adding a new transition:
 *   1. Implement it inside useImageTransition / ResultsVisualCard.
 *   2. Add its id to TransitionType below.
 *   3. Add an entry to TRANSITION_OPTIONS.
 */

export type TransitionType = 'crossfade' | 'pixelBlock' | 'slide';

export interface TransitionOption {
  id: TransitionType;
  label: string;
  description: string;
}

export const TRANSITION_OPTIONS: TransitionOption[] = [
  {
    id: 'crossfade',
    label: 'Crossfade',
    description: 'Fades the new image in over the old one.',
  },
  {
    id: 'pixelBlock',
    label: 'Pixel Block',
    description: 'Reveals the new image in a randomized grid of blocks.',
  },
  {
    id: 'slide',
    label: 'Slide',
    description: 'Slides the new image in from the side.',
  },
];

/** Fallback used when no admin override is stored. */
export const DEFAULT_TRANSITION: TransitionType = 'slide';

/** Transition duration bounds + default, in milliseconds. */
export const TRANSITION_DURATION_MIN_MS = 100;
export const TRANSITION_DURATION_MAX_MS = 2000;
export const TRANSITION_DURATION_STEP_MS = 50;
export const DEFAULT_TRANSITION_DURATION_MS = 500;

/** Auto-cycle hold time (how long each image sits before the next swap). */
export const CYCLE_INTERVAL_MIN_MS = 1000;
export const CYCLE_INTERVAL_MAX_MS = 30000;
export const CYCLE_INTERVAL_STEP_MS = 500;
export const DEFAULT_CYCLE_INTERVAL_MS = 5000;

/** Whether the visualizer auto-starts cycling or waits for Play. */
export const DEFAULT_AUTO_PLAY = false;

export function isTransitionType(value: unknown): value is TransitionType {
  return TRANSITION_OPTIONS.some(opt => opt.id === value);
}

/** Clamp any stored value back into the allowed range. */
export function clampTransitionDuration(value: unknown): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : DEFAULT_TRANSITION_DURATION_MS;
  return Math.min(TRANSITION_DURATION_MAX_MS, Math.max(TRANSITION_DURATION_MIN_MS, Math.round(n)));
}

export function clampCycleInterval(value: unknown): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : DEFAULT_CYCLE_INTERVAL_MS;
  return Math.min(CYCLE_INTERVAL_MAX_MS, Math.max(CYCLE_INTERVAL_MIN_MS, Math.round(n)));
}

export function normalizeAutoPlay(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true') return true;
  if (value === 0 || value === '0' || value === 'false') return false;
  return DEFAULT_AUTO_PLAY;
}
